/** One country-level allow/block control, as served by /admin/country-policies. */
export interface CountryPolicy {
  key: string;
  label: string;
  description: string;
  /** Which code form this policy speaks: phone/IP lists are ISO-2, location lists ISO-3. */
  code_type: 'iso2' | 'iso3' | 'prefix' | 'carrier';
  /**
   * 'block' — the countries on the list are blocked.
   * 'allow' — only the countries on the list are permitted, so absence is a block.
   */
  effect: 'block' | 'allow';
  countries: string[];
  enabled: boolean;
  /** The committed list in the repo, shown beside the effective one. */
  default_countries: string[];
  default_enabled: boolean;
  /** True when an admin override row exists for this policy. */
  is_overridden: boolean;
  differs_from_default: boolean;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface CountryRef {
  iso2: string;
  iso3: string | null;
  name: string;
  flag: string;
  calling_code: string | null;
  continent: string;
  /** 1-20 for the pinned markets (see the API's lib/country-reference), null otherwise. */
  market_rank: number | null;
  market_population: number | null;
  market_note: string | null;
}

export interface CountryPoliciesResponse {
  policies: CountryPolicy[];
  countries: CountryRef[];
}

/** Verification traffic for one country, keyed on the phone country (ISO-2). */
export interface CountryVerificationStats {
  country_iso2: string;
  attempts: number;
  /** Reached the provider — Twilio Verify accepts as `pending`, so that counts. */
  sent: number;
  /** Stopped by a gate before the provider was called. */
  refused: number;
  failed: number;
  verified: number;
  numbers: number;
  attempts_recent: number;
  sent_recent: number;
  numbers_recent: number;
  last_seen: string | null;
}

/** Registered users for one country, keyed on detected location (ISO-3). */
export interface CountryUserStats {
  country_iso3: string;
  users: number;
  users_active: number;
  users_banned: number;
  users_recent: number;
  users_active_30d: number;
  males: number;
  females: number;
  /** Distinct users in this country the community or the rules acted on. */
  users_reported: number;
  users_blocked: number;
  users_flagged: number;
}

export interface PrefixStats {
  prefix: string;
  attempts: number;
  attempts_recent: number;
  numbers: number;
}

export interface CountryPolicyStats {
  window_days: number;
  recorded_since: string | null;
  total_events: number;
  countries: CountryVerificationStats[];
  users: CountryUserStats[];
  prefixes: PrefixStats[];
}
